import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  ReactNode,
} from "react";
import scraperApi from "api";
import { DomainData } from "utils/analytics/types";
import { buildAnalyticsApiParams } from "utils/analytics/filters";
import { DOMAIN_PAGE_SIZE } from "utils/analytics/constants";
import { createPageCache, pageCacheKey } from "utils/analytics/pageCache";
import { useAnalyticsFilters } from "./AnalyticsFilterProvider";
import { useAbortController } from "hooks/useAbortController";
import { useLoading } from "hooks/useLoading";
import { useToast } from "hooks/useToast";

interface DomainDataContextType {
  domainData: DomainData;
  goToDomainPage: (page: number) => void;
  isDomainPageLoading: boolean;
  hasDomainNextPage: boolean;
  domainPageError: string | null;
  retryDomainPage: () => void;
}

const DomainDataContext = createContext<DomainDataContextType | undefined>(
  undefined
);

const EMPTY_DOMAIN_DATA: DomainData = {
  list: [],
  currentPage: 1,
  pageSize: DOMAIN_PAGE_SIZE,
  hasNextPage: false,
};

export const DomainDataProvider = ({ children }: { children: ReactNode }) => {
  const { dateRange, filters } = useAnalyticsFilters();
  const { showError } = useToast();

  const {
    createNewSignal: createNewDomainSignal,
    shouldIgnoreError: shouldIgnoreDomainError,
  } = useAbortController();

  const [domainCache] = useState(() => createPageCache<DomainData>());

  const [domainData, setDomainData] = useState<DomainData>(EMPTY_DOMAIN_DATA);
  const [domainPageError, setDomainPageError] = useState<string | null>(null);

  const { loading: isDomainPageLoading, setLoading: setIsDomainPageLoading } =
    useLoading();

  const fetchDomainPage = useCallback(
    async (page: number, forceRefetch: boolean = false) => {
      if (!dateRange[0] || !dateRange[1]) {
        setIsDomainPageLoading(false);
        return;
      }

      // Abort any in-flight request first, so a late response can't overwrite what we show next
      const signal = createNewDomainSignal();

      const cacheKey = pageCacheKey(dateRange, filters);
      if (domainCache.isNewKey(cacheKey) && !forceRefetch) {
        setDomainData(EMPTY_DOMAIN_DATA);
      }

      const cachedData = forceRefetch ? undefined : domainCache.get(cacheKey, page);
      if (cachedData) {
        setDomainData(cachedData);
        setDomainPageError(null);
        setIsDomainPageLoading(false);
        return;
      }

      try {
        setIsDomainPageLoading(true);
        setDomainPageError(null);

        // The endpoint returns no total, so ask for one extra row: if it comes
        // back, there is a next page
        const response = await scraperApi.analytics.getDomainList(
          buildAnalyticsApiParams(dateRange, filters, {
            limit: DOMAIN_PAGE_SIZE + 1,
            offset: (page - 1) * DOMAIN_PAGE_SIZE,
          }),
          { signal }
        );

        if (signal.aborted) return;

        const rows = response?.data || [];
        const newData: DomainData = {
          list: rows.slice(0, DOMAIN_PAGE_SIZE),
          currentPage: page,
          pageSize: DOMAIN_PAGE_SIZE,
          hasNextPage: rows.length > DOMAIN_PAGE_SIZE,
        };

        domainCache.set(cacheKey, page, newData);

        setDomainData(newData);
      } catch (error: any) {
        if (shouldIgnoreDomainError(error)) return;

        setDomainData({
          ...EMPTY_DOMAIN_DATA,
          currentPage: page,
        });
        setDomainPageError(
          error instanceof Error ? error.message : "Failed to load domains"
        );

        showError(
          "Failed to load domains",
          "There was an error loading domain analytics. Please try again."
        );
      } finally {
        if (!signal.aborted) {
          setIsDomainPageLoading(false);
        }
      }
    },
    [
      dateRange,
      filters,
      domainCache,
      shouldIgnoreDomainError,
      createNewDomainSignal,
      showError,
      setIsDomainPageLoading,
    ]
  );

  const hasDomainNextPage = domainData.hasNextPage;

  const goToDomainPage = useCallback(
    (page: number) => {
      fetchDomainPage(page);
    },
    [fetchDomainPage]
  );

  const retryDomainPage = useCallback(() => {
    fetchDomainPage(domainData.currentPage, true);
  }, [fetchDomainPage, domainData.currentPage]);

  const value = useMemo<DomainDataContextType>(
    () => ({
      domainData,
      goToDomainPage,
      isDomainPageLoading,
      hasDomainNextPage,
      domainPageError,
      retryDomainPage,
    }),
    [domainData, goToDomainPage, isDomainPageLoading, hasDomainNextPage, domainPageError, retryDomainPage]
  );

  return (
    <DomainDataContext.Provider value={value}>
      {children}
    </DomainDataContext.Provider>
  );
};

export const useDomainData = () => {
  const context = useContext(DomainDataContext);
  if (context === undefined) {
    throw new Error("useDomainData must be used within a DomainDataProvider");
  }
  return context;
};
